import { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Button } from "../../../components/common";
import { useTranslation } from "../../../i18n/I18nContext";
import { useLocalePath } from "../../../hooks/useLocalePath";
import { fetchCategories } from "../../../services/categoryService";
import { getCategoryPresetImage } from "../../../utils/categoryPresetImages";
import { LANDING_MAX, LANDING_PAD_X } from "./landingLayout";

function resolveCategoryCover(cat, gridIndex = 0) {
  const presets = Array.isArray(cat.presetImages) ? cat.presetImages.filter(Boolean) : [];
  if (presets.length > 1) {
    const idx = (Number(cat.id) + gridIndex * 17) % presets.length;
    return presets[idx];
  }
  if (presets.length === 1) return presets[0];
  if (cat.coverImage) return cat.coverImage;
  return getCategoryPresetImage(cat.slug || cat.code, cat.id || gridIndex);
}

export function LandingCategories() {
  const { t, lang } = useTranslation();
  const lp = useLocalePath();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategories(lang).then(setCategories);
  }, [lang]);

  const visible = categories.slice(0, 6);

  return (
    <section
      id="categories"
      aria-labelledby="categories-title"
      className="w-full py-12 sm:py-14 lg:py-[76px]"
    >
      <div className={`${LANDING_MAX} ${LANDING_PAD_X}`}>
        <div className="flex flex-col gap-8 sm:gap-10">
          <div className="flex flex-col gap-5 text-center sm:gap-6 lg:gap-[25px]">
            <h2
              id="categories-title"
              className="font-bold leading-[1.3] text-[28px] sm:text-[34px] lg:text-5xl xl:text-[48px]"
            >
              {t("categories.title")}{" "}
              <span className="text-primary">{t("categories.titleHighlight")}</span>
            </h2>
            <p className="text-base leading-relaxed sm:text-lg lg:text-[20px] lg:leading-[32.5px]">
              {t("categories.subtext")}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
            {visible.map((cat, gridIndex) => {
              const coverUrl = resolveCategoryCover(cat, gridIndex);
              return (
                <a
                  key={cat.id}
                  href={lp(`/explore?category=${cat.slug || cat.code}`)}
                  className="group relative h-[200px] overflow-hidden rounded-[20px] shadow-primary-sm transition-all duration-300 hover:shadow-[0_7px_30px_0_rgba(78,129,217,0.17)] lg:h-[220px]"
                >
                  <img
                    src={coverUrl}
                    alt={cat.name || ""}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      if (!e.target.dataset.fallbackApplied) {
                        e.target.dataset.fallbackApplied = "true";
                        e.target.src = getCategoryPresetImage(cat.slug || cat.code, cat.id || 0);
                      }
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10 transition-all duration-300 group-hover:from-black/80" />
                  <div className="absolute inset-0 flex flex-col justify-end p-4 sm:p-5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-[45px] w-[45px] shrink-0 items-center justify-center rounded-[12px] border border-white/30 bg-white/20 backdrop-blur-sm">
                        <Icon icon={cat.icon || "mdi:briefcase"} className="h-6 w-6 text-white" />
                      </div>
                      <div className="min-w-0 flex flex-col">
                        <h3 className="font-semibold text-lg text-white leading-tight lg:text-xl">{cat.name}</h3>
                        {cat.shortDescription && (
                          <p className="mt-0.5 line-clamp-1 text-sm text-white/75">{cat.shortDescription}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
          <Button
            variant="primary"
            label={t("categories.explore")}
            icon="mdi:arrow-right"
            iconDirection="right"
            className="mx-auto rounded-[15px]"
            to={lp("/explore")}
          />
        </div>
      </div>
    </section>
  );
}
